// src/components/PlanosPagamento/EstudantesPlanoDetalhes.jsx

import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper, // Usado
  Button, // Usado
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  Divider, // Usado
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
//import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import WarningIcon from '@mui/icons-material/Warning'; // Usado
import { formatarMoeda } from '../../utils/formatters';
import planosPagamentoService from '../../services/planosPagamentoService';
import { useParams, useNavigate } from 'react-router-dom';

// --- Componentes Auxiliares com PropTypes ---

/**
 * Componente StatusChip
 * Exibe um Chip com base no status fornecido.
 */
const StatusChip = ({ status }) => {
  const getStatusConfig = (status) => {
    switch (status) {
      case 'PAGO':
        return { color: 'success', label: 'Pago' };
      case 'ATRASADO':
        return { color: 'error', label: 'Atrasado' };
      case 'PENDENTE':
        return { color: 'warning', label: 'Pendente' };
      default:
        return { color: 'default', label: status };
    }
  };

  const config = getStatusConfig(status);
  return <Chip size="small" color={config.color} label={config.label} />;
};

StatusChip.propTypes = {
  status: PropTypes.string.isRequired,
};

/**
 * Componente RiscoIndicator
 * Exibe um indicador de risco com base no nível e rótulo fornecidos.
 */
const RiscoIndicator = ({ nivel, label }) => {
  const getColor = (nivel) => {
    switch (nivel) {
      case 'BAIXO':
        return 'success';
      case 'MÉDIO':
        return 'warning';
      case 'ALTO':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Box display="flex" alignItems="center" gap={1}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Chip size="small" color={getColor(nivel)} label={nivel} />
    </Box>
  );
};

RiscoIndicator.propTypes = {
  nivel: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
};

// --- Componente Principal ---

const EstudantesPlanoDetalhes = () => {
  const { planoId, estudanteId } = useParams();
  const navigate = useNavigate();

  const [detalhes, setDetalhes] = useState(null);
  const [estatisticasEstudante, setEstatisticasEstudante] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [estudantes, setEstudantes] = useState([]);
  /**
   * Carrega os dados gerais do plano de pagamento.
   */
  const carregarDados = useCallback(async () => {
    setLoading(true);
    try {
      const response = await planosPagamentoService.obterEstatisticasPlano(planoId);
  
      console.log('Resposta estatísticas:', response); // Para debug
  
      if (response?.data?.estatisticas?.estudantes) {
        setDetalhes(response.data);
        setEstudantes(response.data.estatisticas.estudantes);
      } else {
        setError('Falha ao carregar os dados do plano de pagamento.');
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      setError('Falha ao carregar os dados do plano de pagamento.');
    } finally {
      setLoading(false);
    }
  }, [planoId]);
  /**
   * Carrega as estatísticas do estudante selecionado.
   */
  const carregarEstatisticasEstudante = useCallback(async () => {
    if (!estudanteId) {
      setEstatisticasEstudante(null);
      return;
    }

    setLoading(true);
    setError(null); // Resetar erro antes de tentar carregar
    try {
      const response = await planosPagamentoService.obterDetalhesEstudante(planoId, estudanteId);

      if (response && response.data) {
        setEstatisticasEstudante(response.data);
      } else {
        setError('Dados do estudante não encontrados.');
      }
    } catch (error) {
      console.error('Erro ao carregar estatísticas do estudante:', error);
      setError('Falha ao carregar as estatísticas do estudante.');
    } finally {
      setLoading(false);
    }
  }, [planoId, estudanteId]);

  // Carrega os dados gerais do plano ao montar o componente
  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  // Carrega as estatísticas do estudante quando o estudanteId muda
  useEffect(() => {
    carregarEstatisticasEstudante();
  }, [carregarEstatisticasEstudante]);

  /**
   * Manipula o download do recibo de pagamento.
   * @param {string} pagamentoId - ID do pagamento para baixar o recibo.
   */
  const handleDownloadRecibo = (pagamentoId) => {
    planosPagamentoService
      .downloadRecibo(pagamentoId)
      .then((response) => {
        window.open(response.url, '_blank');
      })
      .catch((error) => {
        console.error('Erro ao baixar recibo:', error);
        setError('Falha ao baixar o recibo.');
      });
  };

  // --- Funções de Renderização das Seções ---

  const renderIndicadoresDesempenho = (indicadores) => (
    <Grid item xs={12}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Indicadores de Desempenho
          </Typography>
          <Grid container spacing={3}>
            {/* Pontualidade e Status */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Pontualidade
                    </Typography>
                    <Typography variant="h6" color="primary">
                      {indicadores.pontualidade}%
                    </Typography>
                  </Box>
                  <StatusChip status={indicadores.indicadores.statusPagador} />
                </Box>
              </Card>
            </Grid>

            {/* Risco de Inadimplência */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Análise de Risco
                </Typography>
                <Box display="flex" flexDirection="column" gap={1}>
                  <RiscoIndicator
                    nivel={indicadores.indicadores.detalhesRisco.porAtraso}
                    label="Por Atraso"
                  />
                  <RiscoIndicator
                    nivel={indicadores.indicadores.detalhesRisco.porValor}
                    label="Por Valor"
                  />
                  <RiscoIndicator
                    nivel={indicadores.indicadores.detalhesRisco.porTempo}
                    label="Por Tempo"
                  />
                </Box>
              </Card>
            </Grid>

            {/* Métricas de Pagamento */}
            <Grid item xs={12} md={4}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Prestações
                </Typography>
                <Typography variant="h6">
                  {indicadores.prestacoesVencidas} / {indicadores.totalPrestacoes}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Vencidas / Total
                </Typography>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Valor Pago
                </Typography>
                <Typography variant="h6" color="success.main">
                  {formatarMoeda(indicadores.valorTotalPago)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  de {formatarMoeda(indicadores.valorTotalPrevisto)} ({indicadores.percentualPago}%)
                </Typography>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Média de Atraso
                </Typography>
                <Typography variant="h6" color={indicadores.mediaAtraso > 0 ? 'error.main' : 'success.main'}>
                  {indicadores.mediaAtraso} dias
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Grid>
  );

  const renderInformacoesVinculo = (vinculo, plano) => (
    <Grid item xs={12}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Informações do Vínculo
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Data do Vínculo
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <CalendarTodayIcon color="action" fontSize="small" />
                  <Typography>
                    {new Date(vinculo.dataVinculo).toLocaleDateString()}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Status do Vínculo
                </Typography>
                <StatusChip status={vinculo.status} />
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ mb: 2 }}>
              <Box sx={{ mb: 2 }}>
  <Typography variant="body2" color="text.secondary">
    Valor Total Vinculado
  </Typography>
  <Typography>
    {formatarMoeda(plano.valorTotal)}
  </Typography>
</Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Grid>
  );

  const renderResumoFinanceiro = (historicoCompleto) => (
    <Grid item xs={12}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Resumo Financeiro
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Total de Prestações
                </Typography>
                <Typography variant="h6">
                  {historicoCompleto.resumo.totalPrestacoes}
                </Typography>
                <Typography variant="caption" color="error.main">
                  {historicoCompleto.resumo.prestacoesEmAtraso} em atraso
                </Typography>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Valor Total
                </Typography>
                <Typography variant="h6">
                  {formatarMoeda(historicoCompleto.resumo.valorTotal)}
                </Typography>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Valor Pago
                </Typography>
                <Typography variant="h6" color="success.main">
                  {formatarMoeda(historicoCompleto.resumo.valorPago)}
                </Typography>
                <Typography variant="caption">
                  {historicoCompleto.resumo.percentualPago}% do total
                </Typography>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Valor Pendente
                </Typography>
                <Typography variant="h6" color="error.main">
                  {formatarMoeda(historicoCompleto.resumo.valorPendente)}
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Grid>
  );

  const renderProximosVencimentos = (previsoes) => (
    <Grid item xs={12}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Próximos Vencimentos
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Prestação</TableCell>
                  <TableCell>Data Vencimento</TableCell>
                  <TableCell align="right">Valor</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {previsoes.proximosVencimentos.map((prestacao) => (
                  <TableRow key={prestacao.id}>
                    <TableCell>{prestacao.mes}</TableCell>
                    <TableCell>
                      {new Date(prestacao.dataVencimento).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="right">
                      {formatarMoeda(prestacao.valor)}
                    </TableCell>
                    <TableCell>
                      <StatusChip status={prestacao.status} />
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={2}>
                    <Typography variant="subtitle2">Total Previsto</Typography>
                  </TableCell>
                  <TableCell align="right" colSpan={2}>
                    <Typography variant="subtitle2">
                      {formatarMoeda(previsoes.previsaoTotal)}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Grid>
  );

  const renderHistoricoPagamentos = (historico) => (
    <Grid item xs={12}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Histórico de Pagamentos
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Prestação</TableCell>
                  <TableCell>Data Vencimento</TableCell>
                  <TableCell align="right">Valor</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historico.pagamentos.map((prestacao) => (
                  <TableRow key={prestacao.id}>
                    <TableCell>{prestacao.mes}</TableCell>
                    <TableCell>
                      {new Date(prestacao.dataVencimento).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="right">
                      {formatarMoeda(prestacao.valor)}
                    </TableCell>
                    <TableCell>
                      <StatusChip status={prestacao.status} />
                    </TableCell>
                    <TableCell>
                      {prestacao.status === 'PAGO' && (
                        <IconButton
                          size="small"
                          onClick={() => handleDownloadRecibo(prestacao.id)}
                          title="Baixar Recibo"
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Grid>
  );

  // --- Renderização Condicional para Loading e Erro ---

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" icon={<WarningIcon />}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!estatisticasEstudante) {
    return null;
  }

  // --- Renderização Principal do Componente ---

  return (
    <Paper sx={{ p: 3, elevation: 3 }}>
      {/* Seletor de Estudante */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container alignItems="center" spacing={2}>
            <Grid item xs={12} md={10}>
              <FormControl fullWidth>
                <InputLabel>Filtrar por Estudante</InputLabel>
                <Select
                  value={estudanteId || ''}
                  onChange={(e) =>
                    navigate(`/planos-pagamento/${planoId}/estudante/${e.target.value}`)
                  }
                  label="Filtrar por Estudante"
                >
                  <MenuItem value="">Todos</MenuItem>
                  {estudantes.map((est) => (
  <MenuItem key={est.id} value={est.id}>
    {est.nome}
  </MenuItem>
))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={carregarEstatisticasEstudante}
                startIcon={<DownloadIcon />}
              >
                Atualizar
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Divider sx={{ mb: 3 }} />

      {/* Grade principal com todas as seções */}
      <Grid container spacing={3}>
        {/* Seção de Indicadores */}
        {estatisticasEstudante.indicadoresDesempenho &&
          renderIndicadoresDesempenho(estatisticasEstudante.indicadoresDesempenho)}

        {/* Seção de Informações do Vínculo */}
        {estatisticasEstudante.vinculo &&
          renderInformacoesVinculo(estatisticasEstudante.vinculo, estatisticasEstudante.plano)}

        {/* Seção de Resumo Financeiro */}
        {estatisticasEstudante.historicoCompleto &&
          renderResumoFinanceiro(estatisticasEstudante.historicoCompleto)}

        {/* Seção de Próximos Vencimentos */}
        {estatisticasEstudante.previsoesPagamento &&
          renderProximosVencimentos(estatisticasEstudante.previsoesPagamento)}

        {/* Seção de Histórico de Pagamentos */}
        {estatisticasEstudante.historicoCompleto &&
          renderHistoricoPagamentos(estatisticasEstudante.historicoCompleto)}
      </Grid>
    </Paper>
  );
};

// --- Exportação do Componente Principal ---
export default EstudantesPlanoDetalhes;
